import type { Die, DieFace } from '../../shared/types.js';

const svgNS = 'http://www.w3.org/2000/svg';

const PIP_POSITIONS: Record<DieFace, [number, number][]> = {
  1: [[50, 50]],
  2: [[28, 28], [72, 72]],
  3: [[28, 28], [50, 50], [72, 72]],
  4: [[28, 28], [72, 28], [28, 72], [72, 72]],
  5: [[28, 28], [72, 28], [50, 50], [28, 72], [72, 72]],
  6: [[28, 28], [72, 28], [28, 50], [72, 50], [28, 72], [72, 72]],
};

/** Augen für eine Augenzahl in die (zuvor geleerte) Pip-Gruppe zeichnen.
 *  Sub-Pixel-Asymmetrie, damit Mehr-Augen-Seiten nicht wie eine Schablone wirken. */
function drawPips(group: SVGGElement, face: DieFace): void {
  while (group.firstChild) group.removeChild(group.firstChild);
  for (const [cx, cy] of PIP_POSITIONS[face]) {
    const dx = Math.sin(cx * cy) * 0.6;
    const dy = Math.cos(cx * cy) * 0.6;
    const pip = document.createElementNS(svgNS, 'circle');
    pip.setAttribute('cx', String(cx + dx));
    pip.setAttribute('cy', String(cy + dy));
    pip.setAttribute('r', '6.5');
    pip.setAttribute('class', 'die-pip');
    pip.setAttribute('filter', 'url(#pip-carved)');
    group.appendChild(pip);
  }
}

export function renderDie(die: Die, opts: {
  index: number;
  selectable: boolean;
  onClick?: (i: number) => void;
  /** Würfel-Augen kurz durchscrambeln, bevor sie auf `die.value` landen. */
  scramble?: boolean;
}): HTMLElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'die';
  el.dataset.face = String(die.value);
  el.dataset.held = die.held ? '1' : '0';
  el.dataset.locked = die.locked ? '1' : '0';
  el.disabled = !opts.selectable && !die.held;
  el.setAttribute('aria-label', `Würfel ${die.value}${die.held ? ', ausgewählt' : ''}${die.locked ? ', gewertet' : ''}`);

  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('aria-hidden', 'true');

  // Body — fill via gradient defined in index.html (#bone-default / #bone-held / #bone-locked).
  const rect = document.createElementNS(svgNS, 'rect');
  rect.setAttribute('x', '4'); rect.setAttribute('y', '4');
  rect.setAttribute('width', '92'); rect.setAttribute('height', '92');
  rect.setAttribute('rx', '14'); rect.setAttribute('ry', '14');
  rect.setAttribute('class', 'die-body');
  rect.setAttribute('stroke', 'rgba(60, 40, 25, 0.55)');
  rect.setAttribute('stroke-width', '1.5');
  svg.appendChild(rect);

  // Surface grain layer — subtle fractal-noise texture on top of the body.
  const grain = document.createElementNS(svgNS, 'rect');
  grain.setAttribute('x', '4'); grain.setAttribute('y', '4');
  grain.setAttribute('width', '92'); grain.setAttribute('height', '92');
  grain.setAttribute('rx', '14'); grain.setAttribute('ry', '14');
  grain.setAttribute('fill', '#000');
  grain.setAttribute('filter', 'url(#bone-grain)');
  grain.setAttribute('opacity', '0.55');
  grain.setAttribute('class', 'die-grain');
  svg.appendChild(grain);

  // Pips — carved-into-surface look (filter).
  const pips = document.createElementNS(svgNS, 'g');
  pips.setAttribute('class', 'die-pips');
  svg.appendChild(pips);
  drawPips(pips, die.value);
  el.appendChild(svg);

  if (opts.scramble) startScramble(pips, die.value);

  if (opts.onClick) {
    el.addEventListener('click', () => opts.onClick!(opts.index));
  }
  return el;
}

/** Lässt die Augen ~5× zufällig springen und landet dann auf der echten Zahl.
 *  Selbst-abbrechend, sobald das Element aus dem DOM fliegt (Re-Render). */
function startScramble(group: SVGGElement, finalFace: DieFace): void {
  const swaps = 5;
  let i = 0;
  const tick = (): void => {
    if (!group.isConnected) return;
    if (i >= swaps) { drawPips(group, finalFace); return; }
    drawPips(group, (1 + ((Math.random() * 6) | 0)) as DieFace); // cosmetic only
    i++;
    setTimeout(tick, 70);
  };
  setTimeout(tick, 60);
}
